import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    MISSING_FIELDS_GATE_FALLBACK_COPY,
    TAXONOMY_SERVICE_CLIENT_PREFIX
} from '@app/exports/models/constants/missing-fields.constants';
import {
    MissingFieldsGate,
    MissingFieldsGateReason,
    MissingFieldsGateResponse
} from '@app/exports/services/responses/missing-fields-gate.response';

/**
 * Stateless helpers for the "Add missing fields" flow (FP-16828): persisted taxonomy keys, gate
 * copy, and mapping a failed gate HTTP call onto an unsupported response.
 *
 * Lives in `root` because it holds no instance state and is shared by the button, popover, and
 * mapping tab — none of which share the drawer-scoped {@link AddMissingFieldsStateService}.
 */
@Injectable({
    providedIn: 'root'
})
export class MissingFieldsService {

    /**
     * `taxonomy_service_keys` is persisted as JSON and can arrive already decoded or as a string.
     * Unparseable values are treated as empty so a bad snapshot cannot drop later writes.
     */
    parseTaxonomyServiceKeys(raw: unknown): Record<string, string> {
        if (raw && typeof raw === 'object') {
            return raw as Record<string, string>;
        }

        if (typeof raw === 'string' && raw !== '') {
            try {
                const parsed: unknown = JSON.parse(raw);
                return parsed && typeof parsed === 'object' ? parsed as Record<string, string> : {};
            } catch {
                return {};
            }
        }

        return {};
    }

    /**
     * Convert a TaaS listing `client` value (`taxonomy_service:{key}:{version}`) to the
     * `taxonomy_key:version` form stored in exports.taxonomy_service_keys. Values that are
     * already in that form pass through.
     */
    normalizeChannelKey(clientValue: string): string {
        return clientValue.startsWith(TAXONOMY_SERVICE_CLIENT_PREFIX)
            ? clientValue.slice(TAXONOMY_SERVICE_CLIENT_PREFIX.length)
            : clientValue;
    }

    /**
     * Popover / toast copy for a gate reason code.
     *
     * The backend returns only the stable `reason` code, so the wording lives here: copy changes
     * ship with a frontend build, can be localised, and cannot drift between two sources. An
     * unrecognised or missing code falls back rather than rendering an empty body.
     */
    gateCopy(
        reason: MissingFieldsGateReason | null | undefined,
        taxonomyKey?: string | null
    ): string {
        switch (reason) {
            case MissingFieldsGateReason.NoDestination:
                return 'Select a destination first.';
            case MissingFieldsGateReason.NoKeys:
                return 'This export doesn\'t have a category field. Add one to view missing fields.';
            case MissingFieldsGateReason.NoCategoryColumn:
                return 'This export\'s taxonomy doesn\'t name a category field. Set one to view missing fields.';
            case MissingFieldsGateReason.TaxonomyNotInTaas: {
                const named: string = taxonomyKey?.trim() ? taxonomyKey.trim() : 'a taxonomy';

                return `This export is tied to ${named}, which is not available in Taxonomy Service yet. `
                    + 'Missing fields can be loaded only for taxonomies that are live there.';
            }
            case MissingFieldsGateReason.NoLiveTaxonomies:
                return 'There are no live taxonomies in Taxonomy Service to use for this export, '
                    + 'so missing fields can\'t be loaded.';
            case MissingFieldsGateReason.TaasUnavailable:
                return 'Taxonomy Service didn\'t respond. Try again in a few minutes.';
            default:
                return MISSING_FIELDS_GATE_FALLBACK_COPY;
        }
    }

    /**
     * Map a failed gate HTTP call to an unsupported response.
     *
     * Status 0 (network / cancelled) and 5xx mean Taxonomy Service (or the proxy to it) did not
     * answer, so the reason is `taas_unavailable`. A 4xx is a client/contract problem — leave
     * `reason` unset so the popover uses the generic fallback rather than blaming TaaS.
     */
    gateFromHttpError(error: unknown): MissingFieldsGateResponse {
        const status: number = error instanceof HttpErrorResponse ? error.status : 0;
        const taasDown: boolean = status === 0 || status >= 500;

        return {
            gate: MissingFieldsGate.Unsupported,
            reason: taasDown ? MissingFieldsGateReason.TaasUnavailable : undefined
        };
    }
}
